import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { initializeStore } from './infrastructure/state/useRadarStore';
import './index.css';

// Lazy load components for code splitting
const HomePage = lazy(() =>
  import('./infrastructure/ui/HomePage').then(module => ({
    default: module.HomePage,
  }))
);
const HomePageErrorBoundary = lazy(() =>
  import('./infrastructure/ui/HomePage').then(module => ({
    default: module.HomePageErrorBoundary,
  }))
);
const RadarView = lazy(() =>
  import('./infrastructure/ui/RadarView').then(module => ({
    default: module.RadarView,
  }))
);
const RadarList = lazy(() =>
  import('./infrastructure/ui/components/RadarList').then(module => ({
    default: module.RadarList,
  }))
);
const MainLayout = lazy(() =>
  import('./infrastructure/ui/layout/MainLayout').then(module => ({
    default: module.MainLayout,
  }))
);
const CreateRadarForm = lazy(() =>
  import('./infrastructure/ui/components/CreateRadarForm').then(module => ({
    default: module.CreateRadarForm,
  }))
);

// Initialize application components following hexagonal architecture
const initializeApplication = async () => {
  // console.log('[App] Starting application initialization');
  try {
    // Initialize the store with the repository asynchronously
    // console.log('[App] Initializing store');
    await initializeStore();
    // console.log('[App] Store initialization complete');

    // Render the application
    // console.log('[App] Rendering application');
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    root.render(
      <React.StrictMode>
        <BrowserRouter>
          <Suspense fallback={<div>Loading application...</div>}>
            <MainLayout>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Suspense fallback={<div>Loading home page...</div>}>
                      <HomePageErrorBoundary>
                        <HomePage />
                      </HomePageErrorBoundary>
                    </Suspense>
                  }
                />
                <Route
                  path="/radars"
                  element={
                    <Suspense fallback={<div>Loading radar list...</div>}>
                      <RadarList tenantId="default" teamId="teamX" />
                    </Suspense>
                  }
                />
                <Route
                  path="/radars/new"
                  element={
                    <Suspense fallback={<div>Loading form...</div>}>
                      <CreateRadarForm />
                    </Suspense>
                  }
                />
                <Route
                  path="/radars/:id"
                  element={
                    <Suspense fallback={<div>Loading radar view...</div>}>
                      <RadarView />
                    </Suspense>
                  }
                />
              </Routes>
            </MainLayout>
          </Suspense>
        </BrowserRouter>
      </React.StrictMode>
    );
    // console.log('[App] Application rendered successfully');
  } catch (error) {
    // console.log('[App] Failed to initialize application:', error);

    // Render a fallback UI for critical errors
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    root.render(
      <div className="error-container">
        <h1>Application Error</h1>
        <p>The application failed to initialize. Please try again later.</p>
        {error instanceof Error && (
          <p className="error-details">{error.message}</p>
        )}
      </div>
    );
  }
};

// Start the application
// console.log('[App] Starting application');
initializeApplication().catch(error => {
  // console.error('[App] Critical application error:', error);
});
